import moment from 'moment'
import styled from 'styled-components'
import React, { useMemo, } from 'react'

import { AdminRelatedLink, } from '@components'

import { ControlRoomRequestingOrder, } from '@types'

interface Props {
  order: ControlRoomRequestingOrder
}

const Container = styled.div`
  border-bottom: 1px solid rgb(227, 230, 235);
  padding: 14px 16px;
  position: relative;

  &.needs-reminder {
    background-color: rgb(255, 247, 247);
  }

  &.needs-reminder::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: rgb(220, 53, 69);
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .top-row {
    align-items: flex-start;
  }

  .order-id {
    font-size: 16px;
    font-weight: 700;
    color: rgb(44, 62, 80);
  }

  .badge-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  .badge {
    border-radius: 999px;
    padding: 4px 9px;
    font-size: 12px;
    font-weight: 700;
  }

  .dispatch-badge {
    background-color: rgb(232, 244, 253);
    color: rgb(22, 119, 255);
  }

  .dispatch-badge.assigned {
    background-color: rgb(236, 248, 237);
    color: rgb(43, 123, 59);
  }

  .dispatch-badge.in-progress {
    background-color: rgb(255, 244, 229);
    color: rgb(180, 95, 0);
  }

  .status-badge {
    background-color: rgb(241, 244, 247);
    color: rgb(76, 90, 106);
  }

  .status-badge.waiting {
    background-color: rgb(244, 246, 248);
    color: rgb(84, 96, 110);
  }

  .status-badge.working {
    background-color: rgb(232, 244, 253);
    color: rgb(22, 119, 255);
  }

  .status-badge.done {
    background-color: rgb(236, 248, 237);
    color: rgb(43, 123, 59);
  }

  .reminder-badge {
    background-color: rgb(255, 235, 238);
    color: rgb(198, 40, 40);
  }

  .section {
    margin-top: 10px;
  }

  .label {
    display: block;
    margin-bottom: 3px;
    font-size: 12px;
    font-weight: 700;
    color: rgb(125, 138, 153);
  }

  .value {
    color: rgb(63, 74, 88);
    line-height: 1.45;
    font-size: 14px;
    word-break: break-word;
  }

  .value.address {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .reminder-summary {
    margin-top: 10px;
    border-radius: 8px;
    background-color: rgb(255, 235, 238);
    padding: 10px 12px;
    color: rgb(165, 28, 48);
    font-size: 13px;
    font-weight: 700;
  }

  .bottom-row {
    margin-top: 12px;
    align-items: flex-end;
  }

  .assigned-agent {
    flex: 1;
  }

  .meta {
    text-align: right;
    color: rgb(120, 132, 146);
    font-size: 12px;
    line-height: 1.45;
  }
`

const REQUESTING_TYPE_LABELS = {
  EVALUATION_DELIVERY: '평카탁송',
  INSPECTION_DELIVERY: '검수탁송',
  ONLY_DELIVERY: '탁송만',
}

const DISPATCH_STATE_LABELS = {
  unassigned: '미배차',
  assigned: '배차완료',
  in_progress: '진행중',
}

const STATUS_LABELS = {
  WAITING_AGENT: '평가사 대기',
  WAITING_WORKING: '평가 작업 대기',
  EVALUATING: '평가 진행중',
  EVALUATION_DONE: '평가 완료',
  WAITING_DELIVERER: '탁송기사 대기',
  WAITING_DELIVERY_WORKING: '탁송 작업 대기',
  DELIVERING: '탁송 진행중',
  DELIVERING_DONE: '탁송 완료',
}

const STATUS_BADGE_CLASS_NAMES = {
  WAITING_AGENT: 'waiting',
  WAITING_WORKING: 'working',
  EVALUATING: 'working',
  EVALUATION_DONE: 'done',
  WAITING_DELIVERER: 'waiting',
  WAITING_DELIVERY_WORKING: 'working',
  DELIVERING: 'working',
  DELIVERING_DONE: 'done',
}

const DISPATCH_BADGE_CLASS_NAMES = {
  unassigned: '',
  assigned: 'assigned',
  in_progress: 'in-progress',
}

function ControlRoomRequestingOrderSummary(props: Props) {
  const { order, } = props

  const dispatchStateLabel = useMemo(
    () => DISPATCH_STATE_LABELS[order.dispatch_state],
    [ order.dispatch_state, ]
  )

  const statusLabel = useMemo(
    () => STATUS_LABELS[order.status],
    [ order.status, ]
  )

  const requestingTypeLabel = useMemo(
    () => REQUESTING_TYPE_LABELS[order.type],
    [ order.type, ]
  )

  const statusBadgeClassName = useMemo(
    () => STATUS_BADGE_CLASS_NAMES[order.status],
    [ order.status, ]
  )

  const dispatchBadgeClassName = useMemo(
    () => DISPATCH_BADGE_CLASS_NAMES[order.dispatch_state],
    [ order.dispatch_state, ]
  )

  const sourceAddress = useMemo(
    () => {
      if(!order.source) {
        return '-'
      }

      return order.source.detail_address ?
        `${ order.source.road_address } ${ order.source.detail_address }` :
        order.source.road_address
    },
    [ order.source, ]
  )

  const destinationAddress = useMemo(
    () => {
      if(!order.destination) {
        return '-'
      }

      return order.destination.detail_address ?
        `${ order.destination.road_address } ${ order.destination.detail_address }` :
        order.destination.road_address
    },
    [ order.destination, ]
  )

  const assignedAgentText = useMemo(
    () => {
      if(!order.assigned_agent || !order.assigned_agent_id) {
        return '배정 대기'
      }

      return null
    },
    [ order.assigned_agent, order.assigned_agent_id, ]
  )

  const assignedAtText = useMemo(
    () => {
      if(order.dispatch_state === 'unassigned') {
        return null
      }

      if(order.assigned_at) {
        return `배정 ${ moment(order.assigned_at).format('MM-DD HH:mm') }`
      }

      return '배정 시각 미기록'
    },
    [ order.dispatch_state, order.assigned_at, ]
  )

  return (
    <Container className={ order.needs_dispatch_reminder ? 'needs-reminder' : '' }>
      <div className='row top-row'>
        <div className='order-id'>
          오더 #
          <AdminRelatedLink
            id={ order.id }
            linkType='requesting'>
            { order.id }
          </AdminRelatedLink>
        </div>
        <div className='badge-container'>
          <span className={ `badge dispatch-badge ${ dispatchBadgeClassName }` }>{ dispatchStateLabel }</span>
          <span className={ `badge status-badge ${ statusBadgeClassName }` }>{ statusLabel }</span>
          {
            order.needs_dispatch_reminder && (
              <span className='badge reminder-badge'>
                { order.dispatch_delay_minutes }분 미배차
              </span>
            )
          }
        </div>
      </div>

      {
        order.needs_dispatch_reminder && (
          <div className='reminder-summary'>
            30분 이상 미배차 상태입니다. 빠른 담당자 확인이 필요합니다.
          </div>
        )
      }

      <div className='section'>
        <span className='label'>오더 유형</span>
        <div className='value'>{ requestingTypeLabel }</div>
      </div>

      <div className='section'>
        <span className='label'>출발지</span>
        <div className='value address' title={ sourceAddress }>{ sourceAddress }</div>
      </div>

      <div className='section'>
        <span className='label'>도착지</span>
        <div className='value address' title={ destinationAddress }>{ destinationAddress }</div>
      </div>

      <div className='row bottom-row'>
        <div className='assigned-agent'>
          <span className='label'>담당자</span>
          <div className='value'>
            {
              order.assigned_agent && order.assigned_agent_id ? (
                <>
                  <AdminRelatedLink
                    id={ order.assigned_agent_id }
                    linkType='agent'>
                    { order.assigned_agent.name }
                  </AdminRelatedLink>
                  {
                    order.assigned_agent.mobile_number ? ` (${ order.assigned_agent.mobile_number })` : ''
                  }
                </>
              ) : assignedAgentText
            }
          </div>
        </div>
        <div className='meta'>
          <div>등록 { moment(order.created_at).format('MM-DD HH:mm') }</div>
          <div>갱신 { moment(order.updated_at).fromNow() }</div>
          {
            assignedAtText && (
              <div>{ assignedAtText }</div>
            )
          }
          {
            order.reminded_at && (
              <div>리마인딩 { moment(order.reminded_at).format('MM-DD HH:mm') }</div>
            )
          }
        </div>
      </div>
    </Container>
  )
}

export default styled(ControlRoomRequestingOrderSummary)``
